import React, { useState } from 'react';
import moment from 'moment';
import Swal from 'sweetalert2';

const StaffDoctorsList = ({
    staffDoctorsList,
    canPrintToken,
    token,
    onRefresh // callback to parent to refresh v15
}) => {
    const [loadingId, setLoadingId] = useState(null);
    const [showGenModal, setShowGenModal] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [patientName, setPatientName] = useState('');
    const [patientMobile, setPatientMobile] = useState('');

    // ── Token List Modal States ──
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [tokenListData, setTokenListData] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [expandedTokenActions, setExpandedTokenActions] = useState({}); // Track which doctor has expanded token actions
    const [listTitle, setListTitle] = useState('');

    if (!staffDoctorsList || staffDoctorsList.length === 0) return null;

    // ─── Native Logic: Shift Calculation ──────────────────────────────────────
    const getShiftInfo = (doctor) => {
        if (!doctor.hours_detail?.time) return { index: 0, timeStr: 'N/A', isDisable: false };

        const now = moment();
        let currentShift = doctor.current_token; // From API
        let upcomingShift = null;
        let timeStr = 'N/A';
        let isDisable = false;

        doctor.hours_detail.time.forEach((hd, idx) => {
            const start = moment(hd.start_time, 'hh:mm A').subtract(30, 'minutes');
            const end = moment(hd.end_time, 'hh:mm A').add(30, 'minutes');

            if (now.isAfter(start) && now.isBefore(end) && currentShift === null) {
                currentShift = idx;
            } else if (now.isBefore(start) && currentShift === null && upcomingShift === null) {
                upcomingShift = idx;
            }
        });

        if (currentShift === null) currentShift = upcomingShift;
        if (currentShift !== null && doctor.hours_detail.time[currentShift]) {
            const sh = doctor.hours_detail.time[currentShift];
            timeStr = `${sh.start_time} - ${sh.end_time}`;
            if (now.isAfter(moment(sh.end_time, 'hh:mm A'))) isDisable = true;
        }

        return { index: currentShift, timeStr, isDisable };
    };

    // ─── Action: Start/Stop Treatment ──────────────────────────────────────────
    const handleStatusToggle = async (doctor, currentSlot, action) => {
        setLoadingId(`${doctor.id}-status`);
        try {
            const res = await fetch("https://tokenly.biswaas.com/api/user/changeDocSlotStatus", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ doctorId: doctor.id, current_slot: currentSlot, actionType: action ? 1 : 0 })
            });
            const json = await res.json();
            if (json.status) onRefresh();
            else Swal.fire('Error', json.message, 'error');
        } catch { Swal.fire('Network Error', 'Please check your internet connection', 'error'); }
        finally { setLoadingId(null); }
    };

    // ─── Action: Online ON/OFF ────────────────────────────────────────────────
    const handleOnlineToggle = async (doctor, shift, currentStatus) => {
        setLoadingId(`${doctor.id}-online`);
        try {
            const res = await fetch("https://tokenly.biswaas.com/api/user/changeTimeSloatStatus", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ doctorId: doctor.id, sloat: shift, isClosed: !currentStatus })
            });
            const json = await res.json();
            if (json.status) onRefresh();
            else Swal.fire('Error', json.message, 'error');
        } catch { Swal.fire('Network Error', 'Please check your internet connection', 'error'); }
        finally { setLoadingId(null); }
    };

    // ─── Action: Call Token (Next/Recall/Skip/Treated) ────────────────────────
    // currentSlot = shift index (0=S1, 1=S2) — same as Android logic
    const handleTokenAction = async (doctor, action, shiftIndex) => {
        setLoadingId(`${doctor.id}-action`);

        const td = doctor.tokenData || {};
        const apiAction = action === 'callNext' ? 'callNext' : 'recall';
        const targetToken = action === 'callNext' ? td.nextToken : td.currentToken;

        try {
            const body = {
                doctorId: doctor.id,
                action: apiAction,
                currentSlot: shiftIndex ?? 0,
                token_no: targetToken || '',
                v15: 1
            };

            const res = await fetch("https://tokenly.biswaas.com/api/doctor/changeTokenStatusV15", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify(body)
            });
            const json = await res.json();

            if (json.status) {
                onRefresh();
            } else {
                Swal.fire('Error', json.message, 'error');
            }
        } catch { Swal.fire('Network Error', 'Please check your internet connection', 'error'); }
        finally { setLoadingId(null); }
    };

    // ─── Manual Generation ────────────────────────────────────────────────────
    const generateManualToken = async () => {
        if (!selectedDoctor) return;
        setLoadingId(`${selectedDoctor.id}-gen`);
        try {
            const res = await fetch("https://tokenly.biswaas.com/api/doctor/genrateToken", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({
                    itemId: selectedDoctor.id,
                    shift: selectedDoctor.current_token || 0,
                    action: 'print',
                    name: patientName,
                    mobile: patientMobile
                })
            });
            const json = await res.json();
            if (json.status) {
                setShowGenModal(false);
                // Silent print if printer data is available
                if (json.data && json.data.token_no) {
                    // printWebReceipt(json.data); // Commented out - replaced with alert
                    const shiftTime = json.data.shift_time || 'N/A';
                    Swal.fire({
                        title: 'Token Generated!',
                        html: `Token <strong>${json.data.token_no}</strong> generated<br>for shift ${shiftTime}`,
                        icon: 'success',
                        confirmButtonColor: '#3085d6'
                    });
                }
                onRefresh();
            } else {
                Swal.fire('Error', json.message, 'error');
            }
        } catch {
            Swal.fire('Network Error', 'Please check your internet connection', 'error');
        }
        finally { setLoadingId(null); }
    };

    // ─── Print Logic (RN Style: genrateTokenAgainPress) ─────────────────────
    const handlePrintClick = (doctor, shiftIndex) => {
        setSelectedDoctor(doctor);
        if (doctor.ask_name === 1) {
            // If ask_name is 1, show modal for Name/Mobile 
            setPatientName('');
            setPatientMobile('');
            setShowGenModal(true);
        } else {
            // Direct API call (Matches isAskName=1 in RN)
            executePrintToken(doctor, shiftIndex, '', '');
        }
    };

    // ─── Web Print Logic (Optimized for 80mm Thermal Printer) ───────────────
    // NOTE: Entire function commented out - replaced with alerts
    /*
    const printWebReceipt = (data) => {
        if (!data || !data.token_no) return;

        const currentDateTime = moment().format('DD/MM/YYYY hh:mm a');
        const w = window.open("", "_blank");
        if (!w) {
            alert("Blocked by browser. Please allow popups for this site.");
            return;
        }

        const receiptHTML = `
            <html>
                <head>
                    <title>Token Receipt #${data.token_no}</title>
                    <style>
                        @page { size: 58mm auto; margin: 0; }
                        body { 
                            margin: 0; 
                            padding: 5mm 2mm; 
                            width: 54mm; 
                            font-family: 'Courier New', Courier, monospace; 
                            font-size: 11px; 
                            line-height: 1.2;
                            color: #000;
                            text-align: center;
                        }
                        .clinic-name { font-weight: bold; font-size: 16px; margin-bottom: 2px; }
                        .doctor-name { font-weight: bold; font-size: 14px; margin-bottom: 10px; }
                        .token-label { font-size: 14px; margin-top: 10px; }
                        .token-no { font-size: 42px; font-weight: bold; margin: 10px 0; line-height: 1; }
                        .info-text { font-size: 11px; margin: 2px 0; }
                        .footer-text { font-size: 10px; margin-top: 15px; font-weight: bold; }
                        .system-text { font-size: 9px; margin-top: 5px; }
                        .divider { border-top: 1px dashed #000; margin: 10px 0; }
                    </style>
                </head>
                <body>
                    <div class="clinic-name">${data.clinicName || ''}</div>
                    ${data.doctorName && data.doctorName.toLowerCase() !== (data.clinicName || '').toLowerCase()
                ? `<div class="doctor-name">${data.doctorName}</div>`
                : '<div style="margin-bottom:10px;"></div>'}
                    
                    <div class="token-label">Token No.</div>
                    <div class="token-no">${data.token_no}</div>
                    
                    <div class="info-text">${currentDateTime}</div>
                    <div class="info-text">Valid between: ${data.shift_time || ''}</div>
                    
                    <div class="footer-text">* Valid for 1 Patient only *</div>
                    <div class="system-text">* TOKENLY - Online Token System</div>
                    
                    <div class="divider"></div>
                </body>
            </html>
        `;

        w.document.write(receiptHTML);
        w.document.close();

        setTimeout(() => {
            w.print();
            w.close();
        }, 500);
    };
    */

    const executePrintToken = async (doctor, shiftIndex, name, mobile, isSMS = 0) => {
        setLoadingId(`${doctor.id}-print`);
        const { timeStr } = getShiftInfo(doctor);

        // If name and mobile are empty, use default values
        const tokenName = (!name || name.trim() === '') ? 'In-Clinic' : name;
        const tokenMobile = (!mobile || mobile.trim() === '') ? 'In-Clinic' : mobile;

        try {
            const res = await fetch("https://tokenly.biswaas.com/api/doctor/genrateToken", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({
                    itemId: doctor.id,
                    shift: shiftIndex ?? 0,
                    action: 'print',
                    name: tokenName,
                    mobile: tokenMobile,
                    isSMS: isSMS,
                    printerType: 'blu',
                    shift_time: timeStr
                })
            });
            const json = await res.json();
            if (json.status) {
                setShowGenModal(false);

                // MIRROR NATIVE: If printing (isSMS=0) and token data exists, print SILENTLY
                if (isSMS === 0 && json.data && json.data.token_no) {
                    // printWebReceipt(json.data); // Commented out - replaced with alert
                    const shiftTime = json.data.shift_time || 'N/A';
                    Swal.fire({
                        title: 'Token Generated!',
                        html: `Token <strong>${json.data.token_no}</strong> generated<br>for shift ${shiftTime}`,
                        icon: 'success',
                        confirmButtonColor: '#3085d6'
                    });
                } else {
                    // Show confirmation for SMS generated success
                    Swal.fire({
                        title: 'Success!',
                        text: json.message,
                        icon: 'success',
                        confirmButtonColor: '#3085d6'
                    });
                }

                onRefresh();
            } else {
                Swal.fire('Error', json.message, 'error');
            }
        } catch { Swal.fire('Network Error', 'Please check your internet connection', 'error'); }
        finally { setLoadingId(null); }
    };

    // ─── Fetch Token List (Clinic / Online / Total popup) ─────────────────────────────
    // type: 'clinic' = in-clinic tokens, 'online' = online tokens, 'total' = both online and in-clinic
    const fetchTokenList = async (doctor, type) => {
        const typeLabel = type === 'online' ? 'Online' : type === 'total' ? 'Total' : 'In-Clinic';
        setListTitle(`${typeLabel} Tokens — ${doctor.name_title || ''} ${doctor.fullname}`);
        setTokenListData([]);
        setShowTokenModal(true);
        setListLoading(true);
        try {
            if (type === 'total') {
                // Fetch both online and in-clinic data
                const [onlineRes, clinicRes] = await Promise.all([
                    fetch("https://tokenly.biswaas.com/api/doctor/getClinicTokenList", {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                        body: JSON.stringify({
                            doctorId: doctor.id,
                            actionType: 'online'
                        })
                    }),
                    fetch("https://tokenly.biswaas.com/api/doctor/getClinicTokenList", {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                        body: JSON.stringify({
                            doctorId: doctor.id,
                            actionType: 'inclinic'
                        })
                    })
                ]);

                const [onlineJson, clinicJson] = await Promise.all([onlineRes.json(), clinicRes.json()]);

                let combinedData = [];

                if (onlineJson.status) {
                    let onlineData = Array.isArray(onlineJson.data) ? onlineJson.data : [];
                    const onlineFiltered = onlineData.filter(t => t.user_id !== null);
                    combinedData = [...combinedData, ...onlineFiltered];
                }

                if (clinicJson.status) {
                    let clinicData = Array.isArray(clinicJson.data) ? clinicJson.data : [];
                    const clinicFiltered = clinicData.filter(t => t.user_id === null);
                    combinedData = [...combinedData, ...clinicFiltered];
                }

                // Sort by creation time to show tokens in chronological order
                combinedData.sort((a, b) => {
                    const timeA = moment(a.created_at);
                    const timeB = moment(b.created_at);
                    return timeB.diff(timeA); // Most recent first
                });

                setTokenListData(combinedData);
            } else {
                // Original logic for individual types
                const res = await fetch("https://tokenly.biswaas.com/api/doctor/getClinicTokenList", {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({
                        doctorId: doctor.id,
                        actionType: type // 'online' or 'inclinic'
                    })
                });
                const json = await res.json();
                if (json.status) {
                    let data = Array.isArray(json.data) ? json.data : [];

                    // ── Strict Filtering Logic ──
                    // Online tokens must have a user_id
                    // In-Clinic tokens must have a null user_id
                    const filtered = data.filter(t => {
                        if (type === 'online') {
                            return t.user_id !== null;
                        } else {
                            return t.user_id === null;
                        }
                    });

                    setTokenListData(filtered);
                }
                else console.warn("Token List:", json.message);
            }
        } catch (e) { console.error("fetchTokenList error:", e); }
        finally { setListLoading(false); }
    };

    return (
        <div className="staff-list">
            {staffDoctorsList.map((u, i) => {
                if (u.is_hide_list !== 0) return null;
                const { index: currentShift, timeStr, isDisable } = getShiftInfo(u);
                const currentSlot = u.current_token; // currentSlot is u.current_token in API
                const isTreatmentLive = currentShift !== null && currentShift === currentSlot;

                // Online data parsing
                let isClosedToday = false;
                try {
                    const todaySloat = JSON.parse(u.today_sloat || '[]');
                    const resultSlot = todaySloat.find(t => t.sloat === currentShift);
                    if (resultSlot?.isClosed) isClosedToday = true;
                } catch { /* empty */ }

                const td = u.tokenData || {};

                return (
                    <div key={u.id} className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden" style={{ maxWidth: '530px', margin: '0 auto' }}>
                        <div className="card-header bg-white border-0 py-3 px-4 d-flex align-items-center gap-3">
                            <img src={u.profile_photo_full} className="rounded-circle shadow-sm" style={{ width: '50px', height: '50px', objectFit: 'cover' }} alt={u.fullname} />
                            <div>
                                <h5 className="mb-0 fw-bold">{u.name_title} {u.fullname}</h5>
                                <span className="text-uppercase fw-bold text-dark" style={{ fontSize: '0.95rem' }}>
                                    Shift (S{currentShift !== null ? currentShift + 1 : '?'}) {timeStr}
                                </span>
                            </div>
                        </div>

                        <div className="card-body p-2">
                            {/* Toggle Buttons Row */}
                            <div className="row g-2 mb-2">
                                <div className="col-6">
                                    <button
                                        className={`btn w-100 py-3 rounded-3 fw-bold text-white shadow-sm d-flex flex-column align-items-center justify-content-center ${isTreatmentLive ? 'btn-success' : 'btn-danger'}`}
                                        onClick={() => handleStatusToggle(u, currentShift, !isTreatmentLive)}
                                        disabled={currentShift === null || loadingId === `${u.id}-status`}
                                        style={{ lineHeight: '1.2' }}
                                    >
                                        <span style={{ fontSize: '1.05rem' }}>{isTreatmentLive ? 'Stop' : 'Start'}</span>
                                        <span style={{ fontSize: '0.8rem' }}>Treatment</span>
                                    </button>
                                </div>
                                <div className="col-6">
                                    <button
                                        className={`btn w-100 py-3 rounded-3 fw-bold text-white shadow-sm d-flex flex-column align-items-center justify-content-center ${isDisable || isClosedToday ? 'btn-danger' : 'btn-success'}`}
                                        onClick={() => handleOnlineToggle(u, currentShift, isClosedToday)}
                                        disabled={currentShift === null || isDisable || loadingId === `${u.id}-online`}
                                        style={{ lineHeight: '1.2' }}
                                    >
                                        <span style={{ fontSize: '1.05rem' }}>{isClosedToday ? 'ON' : 'OFF'}</span>
                                        <span style={{ fontSize: '0.8rem' }}>Online Token</span>
                                    </button>
                                </div>
                            </div>

                            {/* Token Live Box */}
                            <div className="bg-primary bg-opacity-10 rounded-3 p-2 mb-2 border border-primary border-opacity-25">

                                {/* Last Token Info Bar — same as native */}
                                {/* Last Token Info Bar removed as requested */}

                                {/* Patient Name(Mobile) - Centered */}
                                <div className="fw-bold mb-2 text-center text-primary" style={{ fontSize: '1.2rem' }}>
                                    Current Token No: <span className="text-dark px-2">{td.currentToken || 'N/A'}</span>
                                </div>

                                {/* Native Logic: Call Token Button */}
                                {currentShift != null && u.appointment_type === 0 && (
                                    <div className="card-body p-2">
                                        {td.totalToken === 0 || (td.currentToken == null && td.nextToken == null) ? (
                                            <div className="text-center">
                                                <p className="text-danger fw-bold mb-0" style={{ fontSize: '1.1rem' }}>
                                                    {td.currentToken === 'N/A' ? 'All token has been completed for today!' : 'No Token for today till now.'}
                                                </p>
                                            </div>
                                        ) : td.currentToken != 'N/A' && td.currentToken != null && td.currentTokenStatus == 0 ? (
                                            <button
                                                className="btn btn-success w-100 py-2 rounded-3 fw-bold shadow-sm"
                                                onClick={() => handleTokenAction(u, 'callNextFrist', currentShift)}
                                                disabled={!isTreatmentLive || loadingId === `${u.id}-action`}
                                                style={{ fontSize: '0.85rem' }}>
                                                Call Token No. {td.currentToken}
                                            </button>
                                        ) : (
                                            <>


                                                {!expandedTokenActions[u.id] ? (
                                                    <button
                                                        className="btn btn-primary w-100 py-2 rounded-3 fw-bold shadow-sm"
                                                        onClick={() => setExpandedTokenActions(prev => ({
                                                            ...prev,
                                                            [u.id]: true
                                                        }))}
                                                        disabled={!isTreatmentLive || loadingId === `${u.id}-action`}
                                                        style={{ fontSize: '0.95rem', padding: '0.65rem 0' }}>
                                                        Call Token No. {td.currentToken}
                                                    </button>
                                                ) : (
                                                    <div className="row g-1">
                                                        <div className="col-6 text-center">
                                                            <div className="mb-2 fw-bold text-dark border-bottom pb-1 px-1 overflow-hidden" style={{ fontSize: '0.85rem' }}>
                                                                Name: {td.name || 'N/A'}
                                                            </div>
                                                            <button
                                                                className="btn btn-success w-100 py-3 rounded-3 fw-bold shadow-sm"
                                                                onClick={() => handleTokenAction(u, 'callNext', currentShift)}
                                                                disabled={!isTreatmentLive || !td.nextToken || loadingId === `${u.id}-action`}
                                                                style={{ fontSize: '0.8rem' }}>
                                                                Call Next #{td.nextToken ?? 'N/A'}
                                                            </button>
                                                        </div>
                                                        <div className="col-6 text-center">
                                                            <div className="mb-2 fw-bold text-dark border-bottom pb-1 px-1 overflow-hidden" style={{ fontSize: '0.85rem' }}>
                                                                M.No: {td.mobile?.replace(/^\+91/, '') || 'N/A'}
                                                            </div>
                                                            <button
                                                                className="btn btn-danger w-100 py-3 rounded-3 fw-bold shadow-sm"
                                                                onClick={() => handleTokenAction(u, 'recall', currentShift)}
                                                                disabled={!isTreatmentLive || !td.currentToken || loadingId === `${u.id}-action`}
                                                                style={{ fontSize: '0.8rem' }}>
                                                                Recall #{td.currentToken ?? 'N/A'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Last Token Generated Display */}
                            <div className="text-center mb-1 mt-1">
                                <p className="mb-0 fw-bold text-primary" style={{ fontSize: '1.1rem' }}>
                                    Last Token Number Generated : {td.lastToken || 'N/A'}
                                </p>
                            </div>

                            {/* Stats Table Section */}
                            <div className="row text-center row-cols-3 g-0 border-top pt-1">
                                <div className="border-end" style={{ cursor: 'pointer' }}
                                    onClick={() => fetchTokenList(u, 'inclinic')}>
                                    <div className="fw-bold text-success" style={{ fontSize: '0.8rem' }}> View In Clinic</div>
                                    <div className="h6 mb-0 fw-bold" style={{ fontSize: '1rem' }}>{td.inClinicToken || 0}</div>
                                </div>
                                <div className="border-end" style={{ cursor: 'pointer' }}
                                    onClick={() => fetchTokenList(u, 'online')}>
                                    <div className="fw-bold text-primary" style={{ fontSize: '0.8rem' }}>View Online</div>
                                    <div className="h6 mb-0 fw-bold" style={{ fontSize: '1rem' }}>{td.onlineToken || 0}</div>
                                </div>
                                <div style={{ cursor: 'pointer' }}
                                    onClick={() => fetchTokenList(u, 'total')}>
                                    <div className="fw-bold text-danger" style={{ fontSize: '0.8rem' }}>View Total</div>
                                    <div className="h6 mb-0 fw-bold" style={{ fontSize: '1rem' }}>{td.totalToken || 0}</div>
                                </div>
                            </div>
                        </div>

                        {/* Bottom Buttons: Print Token (Exactly like Native) */}
                        {canPrintToken === 1 && (
                            <div className="card-footer bg-white border-0 p-2 pt-0">
                                <button className="btn btn-primary w-100 py-2 rounded-3 fw-bold text-white shadow-sm"
                                    onClick={() => handlePrintClick(u, currentShift)}
                                    disabled={currentShift === null || loadingId === `${u.id}-print`}
                                    style={{ fontSize: '0.95rem', padding: '0.65rem 0' }}>
                                    {loadingId === `${u.id}-print` ? '...' : (u.ask_name === 1 ? 'Print Token' : 'Print Token')}
                                </button>
                            </div>
                        )}
                    </div>

                );
            })}

            {/* ── Token List Modal (Clinic / Online) ── */}
            {showTokenModal && (
                <div className="position-fixed top-50 start-50 translate-middle bg-white p-4 shadow-lg border rounded-3"
                    style={{ zIndex: 1060, minWidth: '400px', maxWidth: '90%' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                        <h5 className="mb-0 fw-bold text-primary">{listTitle}</h5>
                        <button type="button" className="btn-close" onClick={() => setShowTokenModal(false)}></button>
                    </div>

                    {listLoading ? (
                        <div className="text-center p-4">
                            <div className="spinner-border text-primary" role="status"></div>
                            <div className="mt-2 text-muted">Loading...</div>
                        </div>
                    ) : tokenListData.length > 0 ? (
                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="table table-sm table-hover">
                                <thead className="bg-light sticky-top">
                                    <tr>
                                        <th>Token</th>
                                        <th>Patient</th>
                                        <th>Mobile</th>
                                        <th className="text-end">Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tokenListData.map((t, idx) => (
                                        <tr key={idx}>
                                            <td><span className="badge bg-primary">{t.token_no}</span></td>
                                            <td className="fw-bold">{t.user_id ? (t.name || 'Anonymous') : (t.name || 'In-Clinic')}</td>
                                            <td className="small text-muted">{t.user_id ? (t.mobile?.replace(/^\+91/, '') || '—') : (t.mobile?.replace(/^\+91/, '') || 'In-Clinic')}</td>
                                            <td className="text-end small">{t.created_at ? moment(t.created_at).format('hh:mm A') : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center p-4 text-muted border rounded">
                            <i className="bi bi-inbox fs-3 d-block mb-2"></i>
                            No tokens found.
                        </div>
                    )}

                    <div className="mt-3 pt-3 border-top">
                        <button className="btn btn-primary w-100 fw-bold" onClick={() => setShowTokenModal(false)}>CLOSE</button>
                    </div>
                </div>
            )}

            {/* Manual Generation Modal */}
            {showGenModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content rounded-4 border-0 shadow-lg">
                            <div className="modal-header border-0 pb-0">
                                <h5 className="modal-title fw-bold">Manual Token Generation</h5>
                                <button type="button" className="btn-close" onClick={() => setShowGenModal(false)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Patient Name(Optional)</label>
                                    <input type="text" className="form-control rounded-3" placeholder="Enter patient name"
                                        value={patientName} onChange={e => setPatientName(e.target.value)} />
                                </div>
                                <div className="mb-4">
                                    <label className="form-label small fw-bold">Mobile (Optional)</label>
                                    <input type="tel" className="form-control rounded-3" placeholder="10 digit mobile"
                                        maxLength={10} value={patientMobile} onChange={e => setPatientMobile(e.target.value.replace(/\D/g, ''))} />
                                </div>
                                <div className="d-flex flex-column gap-2 mt-2">
                                    <div className="d-flex gap-2">
                                        <button className="btn btn-outline-secondary w-50 py-3 rounded-3 fw-bold" onClick={() => setShowGenModal(false)}>BACK</button>
                                        <button className="btn btn-primary w-50 py-3 rounded-3 fw-bold"
                                            onClick={() => executePrintToken(selectedDoctor, getShiftInfo(selectedDoctor).index, patientName, patientMobile, 0)}>
                                            <i className="bi bi-printer me-1"></i> PRINT
                                        </button>
                                    </div>
                                    <button className="btn btn-success w-100 py-3 rounded-3 fw-bold"
                                        disabled={!patientMobile || patientMobile.length < 10}
                                        onClick={() => executePrintToken(selectedDoctor, getShiftInfo(selectedDoctor).index, patientName, patientMobile, 1)}>
                                        <i className="bi bi-chat-left-dots me-1"></i> SEND SMS
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StaffDoctorsList;
